import { useState, useEffect } from 'react';
import { squadMemberApi } from '../../api/squad-member.api';
import { useAppSelector } from '../../store/hooks';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  ExitToApp,
  HourglassEmpty,
} from '@mui/icons-material';

interface Member {
  id: string;
  status: string;
  registeredAt: string;
  approvedAt?: string;
  leftAt?: string;
  notes?: string;
  player: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    dateOfBirth?: string;
    gender?: string;
    isActive: boolean;
  };
}

interface SquadMemberListProps {
  squadId: string;
  coachId?: string | null;
}

export const SquadMemberList = ({ squadId, coachId }: SquadMemberListProps) => {
  const { user } = useAppSelector((state) => state.auth);
  const [members, setMembers] = useState<Member[]>([]);
  const [squadInfo, setSquadInfo] = useState<{ squadName: string; capacity: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const canManageMembers = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH';
  const canViewMembers = canManageMembers ||
                         user?.role === 'COACH' && user?.id === coachId;

  useEffect(() => {
    fetchMembers();
  }, [squadId]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await squadMemberApi.getSquadMembers(squadId, false);
      const data = response.data.data;
      setSquadInfo({
        squadName: data.squadName,
        capacity: data.capacity,
      });
      setMembers(data.members || []);
    } catch (err) {
      setError('Failed to load squad members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (playerId: string) => {
    setError('');
    setSuccess('');

    try {
      await squadMemberApi.approveMembership(squadId, playerId);
      setSuccess('Membership approved successfully');
      fetchMembers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to approve membership');
      } else {
        setError('Failed to approve membership');
      }
    }
  };

  const handleOpenRejectDialog = (member: Member) => {
    setSelectedMember(member);
    setRejectDialogOpen(true);
    setRejectReason('');
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setSelectedMember(null);
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!selectedMember) return;

    setError('');
    setSuccess('');

    try {
      await squadMemberApi.rejectMembership(squadId, selectedMember.player.id, rejectReason);
      setSuccess('Membership rejected');
      handleCloseRejectDialog();
      fetchMembers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to reject membership');
      } else {
        setError('Failed to reject membership');
      }
    }
  };

  const handleWithdraw = async (playerId: string) => {
    if (!window.confirm('Are you sure you want to withdraw from this squad?')) return;

    setError('');
    setSuccess('');

    try {
      await squadMemberApi.withdrawFromSquad(squadId, playerId);
      setSuccess('Withdrawn from squad successfully');
      fetchMembers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to withdraw');
      } else {
        setError('Failed to withdraw');
      }
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label="Active" color="success" size="small" />;
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" icon={<HourglassEmpty />} />;
      case 'waitlist':
        return <Chip label="Waitlist" color="info" size="small" />;
      case 'withdrawn':
        return <Chip label="Withdrawn" color="default" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const activeCount = members.filter((m) => m.status === 'active').length;
  const pendingCount = members.filter((m) => m.status === 'pending').length;
  const waitlistCount = members.filter((m) => m.status === 'waitlist').length;
  const rejectedCount = members.filter((m) => m.status === 'rejected').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Squad Members
          </Typography>
          {squadInfo && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${activeCount}/${squadInfo.capacity} Active`}
                color={activeCount >= squadInfo.capacity ? 'error' : 'success'}
              />
              {canViewMembers && pendingCount > 0 && (
                <Chip label={`${pendingCount} Pending`} color="warning" />
              )}
              {canViewMembers && waitlistCount > 0 && (
                <Chip label={`${waitlistCount} Waitlist`} color="info" />
              )}
              {canViewMembers && rejectedCount > 0 && (
                <Chip label={`${rejectedCount} Rejected`} color="error" variant="outlined" />
              )}
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {!canViewMembers ? (
          <Typography variant="body2" color="text.secondary">
            Member details are only visible to coaches and administrators.
          </Typography>
        ) : members.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No members yet
          </Typography>
        ) : (
          <List>
            {members.map((member) => (
              <ListItem key={member.id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        {member.player.firstName} {member.player.lastName}
                      </Typography>
                      {getStatusChip(member.status)}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography component="span" variant="body2">
                        {member.player.email}
                      </Typography>
                      <br />
                      <Typography component="span" variant="caption" color="text.secondary">
                        Registered: {new Date(member.registeredAt).toLocaleDateString()}
                      </Typography>
                      {member.approvedAt && (
                        <>
                          {' • '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            Approved: {new Date(member.approvedAt).toLocaleDateString()}
                          </Typography>
                        </>
                      )}
                      {member.notes && (
                        <>
                          <br />
                          <Typography component="span" variant="caption" color="text.secondary">
                            Note: {member.notes}
                          </Typography>
                        </>
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {canManageMembers && member.status === 'pending' && (
                      <>
                        <IconButton
                          edge="end"
                          color="success"
                          onClick={() => handleApprove(member.player.id)}
                          title="Approve"
                        >
                          <CheckCircle />
                        </IconButton>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleOpenRejectDialog(member)}
                          title="Reject"
                        >
                          <Cancel />
                        </IconButton>
                      </>
                    )}
                    {canManageMembers && member.status === 'rejected' && (
                      <IconButton
                        edge="end"
                        color="success"
                        onClick={() => handleApprove(member.player.id)}
                        title="Approve (Reverse Rejection)"
                      >
                        <CheckCircle />
                      </IconButton>
                    )}
                    {(canManageMembers || user?.id === member.player.id) &&
                      member.status === 'active' && (
                        <IconButton
                          edge="end"
                          color="warning"
                          onClick={() => handleWithdraw(member.player.id)}
                          title="Withdraw"
                        >
                          <ExitToApp />
                        </IconButton>
                      )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Membership</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject {selectedMember?.player.firstName}{' '}
            {selectedMember?.player.lastName}'s registration?
          </Typography>
          <TextField
            fullWidth
            label="Reason (optional)"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Provide a reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
